/* eslint-disable no-throw-literal */
/* eslint-disable max-classes-per-file */
import fetch from 'isomorphic-fetch'
import { dataLayer } from 'utils/analytics'
import { isObject, isString, isError, isArray } from 'utils/types'
import { getNormalizedApiHost } from 'utils/hosts'
import { prop, has } from 'ramda'
import { getAccessToken } from 'auth/useAccessToken'
import { generateDevJwt } from 'auth/generateDevJwt'

const VALIDATION_ERROR = 'Validation error'

const REQUEST_INCLUDE_CREDENTIALS = ![false, 'false', 0, '0'].includes(
  process.env.REACT_APP_REQUEST_INCLUDE_CREDENTIALS
)

const TIMEOUT = 300 * 1000 // 5 minutes

export class NetworkError extends Error {
  constructor(...args) {
    super(...args)
    this.name = 'NetworkError'
  }
}

export class NetworkTimeoutError extends NetworkError {
  constructor(...args) {
    super(...args)
    this.name = 'NetworkTimeoutError'
  }
}

const timeout = async (uri, promise, delay = TIMEOUT) => {
  let timeoutRef

  const timer = new Promise((resolve, reject) => {
    timeoutRef = setTimeout(() => {
      const exception = new NetworkTimeoutError(
        `Timeout of ${delay / 1000}s reached for ${uri}`
      )
      reportTimeout(exception)
      reject(exception)
    }, delay)
  })

  return Promise.race([timer, promise]).then((result) => {
    clearTimeout(timeoutRef)
    return result
  })
}

const downloadFile = (blob, fileName) => {
  try {
    if (window?.navigator?.msSaveOrOpenBlob) {
      window.navigator.msSaveOrOpenBlob(blob, fileName)
    } else {
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')

      a.href = url
      a.download = fileName
      a.click()
      window.URL.revokeObjectURL(url)
    }

    return blob
  } catch (error) {
    const err = new Error(error)
    return Promise.reject(err)
  }
}

// All new V3 endpoints
// NOTE: Does not include download functionality. Maybe its own function?
/**
 * @typedef {'GET'|'POST'} Methods
 *
 * @typedef {object} HttpParams
 * @property {string} uri
 * @property {Methods} method
 * @property {string|object} body
 *
 * @param {string|HttpParams} params
 * @returns {Promise<any>}
 */
const makeApiRequest = async (params) => {
  if (!isObject(params) && !isString(params)) {
    throw new Error(
      'Error: Check parameters. apiRequest(params: string|HttpParams): Promise<any>.'
    )
  }

  // create the final uri
  let uri = ''

  // Capture the original relative path before makeUri prepends the host/prefix.
  // This is attached to error objects so handlers (e.g. defaultErrorHandler) can
  // produce human-readable "request to /vehicles/ABC123 failed" messages without
  // leaking internal hostnames or environment-specific prefixes.
  const requestUri = isString(params) ? params : prop('uri', params) || ''

  // Some POST endpoints are semantically read operations (e.g. search routes that
  // use POST only because the query body is too complex for a query string).
  // Callers mark these with `readOnly: true` so error handlers can treat them
  // like GET failures (silent — dev console.warn only) instead of mutation
  // failures (blocking error modal).
  const readOnly = isObject(params) ? Boolean(params.readOnly) : false

  if (isString(params)) {
    uri = makeUri(params)
  } else if (isObject(params)) {
    uri = makeUri(prop('uri', params))
  } else {
    throw new Error('Must include a valid uri string or { uri: string }.')
  }

  // get the method
  const method = has('method', params) ? prop('method', params) : 'GET'

  const requestObject = {
    uri,
    method,
  }

  // handle request body, is string or object
  if (has('body', params)) {
    const body = prop('body', params)

    if (isObject(body) || isArray(body)) {
      requestObject.body = JSON.stringify(body)
    } else if (isString(body)) {
      requestObject.body = body
    } else {
      throw new Error(
        'Error: Body must be string or object. HttpParams { body: string|object }.'
      )
    }
  }

  if (REQUEST_INCLUDE_CREDENTIALS) {
    requestObject.credentials = 'include'
  }

  // Restrict dev-JWT injection to localhost only (matches the isLocalhost guard in
  // NotificationProvider.jsx). On deployed environments the dev JWT must never be
  // sent — infrastructure auth (SiteMinder, Plant Viewer header, MSAL) handles it.
  const isLocalhostRequest =
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1'

  const localDevelopmentHeaders =
    isLocalhostRequest && process.env.REACT_APP_API_X_USERNAME
      ? {
          // Use a dev JWT (Bearer token) instead of the legacy x-username header.
          // The ChecApi handler accepts unsigned JWTs and resolves the username from
          // the preferred_username claim — no ENV_IS_LOCAL_BYPASS compile symbol required.
          Authorization: `Bearer ${generateDevJwt(
            process.env.REACT_APP_API_X_USERNAME
          )}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          Pragma: 'no-cache',
          Expires: '0',
        }
      : {}

  // Get MSAL access token for Bearer authentication
  let authHeader = {}
  try {
    const accessToken = await getAccessToken()
    if (accessToken) {
      authHeader = { Authorization: `Bearer ${accessToken}` }
    }
  } catch (error) {
    console.warn('Failed to get access token:', error)
  }

  // add required headers (custom headers from params can override defaults)
  const customHeaders = params.headers || {}

  requestObject.headers = {
    'Content-Type': 'application/json',
    ...localDevelopmentHeaders,
    ...authHeader,
    ...customHeaders,
  }

  const { download: downloadFilename } = params
  const shouldDownloadFile = Boolean(downloadFilename)

  return timeout(
    uri,
    fetch(uri, requestObject)
      .then(async (response) => {
        // ── Download path ───────────────────────────────────────────────────
        // Check the HTTP status BEFORE converting to a blob. Without this check
        // a non-2xx response (e.g. 401, 403, 500) would be blobbed and passed
        // to downloadFile() as if it were a successful file payload — silently
        // downloading the error response body and never reaching the failure
        // action dispatch path that drives the in-modal / notification error UX.
        if (shouldDownloadFile) {
          if (response.status < 200 || response.status >= 300) {
            // Mirror the error shape produced by the JSON error path below so
            // error handlers (enriched by the outer .catch) receive consistent
            // metadata (err.code, err.status, err.method, err.requestUri).
            if (response.status >= 500 && response.status < 600) {
              const text =
                (await response.text()) || 'Oops, something has gone wrong.'
              const err = new NetworkError(`${text} requesting ${uri}`)
              err.code = response.status
              err.status = response.status
              return Promise.reject(err)
            }
            // 4xx and other non-2xx non-5xx (e.g. 401, 403, 404)
            const err = new NetworkError(
              `Download failed with status ${response.status} requesting ${uri}`
            )
            err.code = response.status
            err.status = response.status
            return Promise.reject(err)
          }
          // Only convert to blob after confirming a 2xx response
          return response.blob()
        }
        return response
      })
      .then(async (response) => {
        // file download (blob already validated as 2xx above)
        try {
          if (shouldDownloadFile) {
            return downloadFile(response, downloadFilename)
          }
        } catch (error) {
          const err = new Error(error)
          return Promise.reject(err)
        }

        // handle json
        try {
          if (response.status >= 200 && response.status < 300) {
            // 204 No Content — server succeeded but returned no body.
            // Resolve with an empty object so apiCall.js always receives a
            // response-shaped value. Resolving with undefined (Promise.resolve()
            // with no argument) would cause apiCall to throw when it reads
            // response.data, inverting a successful mutation into a frontend
            // failure that dispatches the error path and shows error UI.
            return response.status === 204
              ? Promise.resolve({})
              : response.json()
          }

          // This is a generic handler for 500.
          // In production, 500 error suppress title and detail info.
          if (response.status >= 500 && response.status < 600) {
            const text =
              (await response.text()) || 'Oops, something has gone wrong.'
            const err = new NetworkError(`${text} requesting ${uri}`)
            err.code = response.status
            err.status = response.status
            return Promise.reject(err)
          }

          // Use await (not return promise.then()) so that a JSON parse failure
          // on a 4xx response is caught by the local catch below, which preserves
          // response.status on the thrown error. Returning the promise chain would
          // bypass this catch — the rejection would propagate to the outer .catch
          // which only attaches method/requestUri, so err.status would be absent
          // and fetchFailureHandler would never redirect on 401/403.
          const jsonData = await response.json()
          const message = `${jsonData.title} - ${jsonData.detail}`
          const error = new Error(message)
          error.code = response.status
          error.status = response.status
          return Promise.reject(error)
        } catch (error) {
          const err = new Error(error)
          // Preserve the HTTP status code on the rethrown error so that
          // fetchFailureHandler can still redirect on 401/403 even when the
          // response body is non-JSON (e.g. a SiteMinder HTML redirect page,
          // an empty body, or any malformed JSON). Without this, the parse
          // error escapes without err.status and fetchFailureHandler's
          // `err.status === 401 || err.status === 403` guard never fires —
          // leaving GET requests silently swallowed by defaultErrorHandler.
          if (response.status) {
            err.status = response.status
            err.code = response.status
          }
          return Promise.reject(err)
        }
      })
      .catch((err) => {
        // throw on any unwrapped errors
        const foundError = isError(err) || err.name === VALIDATION_ERROR
        throw foundError ? err : new Error(err)
      })
  ).catch((err) => {
    // Enrich every error (NetworkTimeoutError, NetworkError, plain Error) with
    // the HTTP method and the original relative URI so error handlers can
    // produce meaningful messages and make routing decisions (e.g. show a toast
    // for GET failures vs. a modal for mutation failures) without needing this
    // information to be passed explicitly through the action/saga layer.
    //
    // We only set these if not already present — custom errors thrown by the
    // service layer (e.g. PtpError) may carry their own metadata and should not
    // be overwritten.
    if (!err.method) err.method = method
    if (!err.requestUri) err.requestUri = requestUri
    if (err.readOnly === undefined) err.readOnly = readOnly
    throw err
  })
}

/**
 *
 * @param {string} uri
 * @returns {string}
 */
const makeUri = (uri) => {
  // get the host from hosts.js or .env
  // getNormalizedApiHost() handles protocol stripping and fallbacks
  const host = getNormalizedApiHost()

  if (!uri.match(/^\//)) uri = `/${uri}`

  // no "/v3/" in route for local development
  const prefix = process.env.REACT_APP_API_PREFIX || ''

  uri = prefix + uri

  // Check if running on localhost (local development with proxy)
  const isLocalhost =
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1'

  if (isLocalhost) {
    // ----------------------------------------------------------------------------
    // Local development: route through the local proxy (which is set up at /api)
    // localhost:<port>/api/version → Proxied to https://<api-host>/v3/version via setupProxy.js
    // Port is determined by the running dev server (configurable via PORT env var)
    // ----------------------------------------------------------------------------
    const port = window.location.port ? `:${window.location.port}` : ''
    uri = `${window.location.protocol}//localhost${port}/api${uri}`
  } else {
    // ----------------------------------------------------------------------------
    // Deployed environments (dev, qa, prod): use full URL with host
    // REACT_APP_API_HOST may include protocol (https://host/v3) or just hostname
    // ----------------------------------------------------------------------------
    const hostHasProtocol = host && host.startsWith('http')
    if (hostHasProtocol) {
      // Host already includes protocol (e.g., https://chec-dtna-uat.cloud.tbintra.net/v3)
      uri = `${host}${uri}`
    } else {
      // Host is just hostname (e.g., chec-dtna.cloud.tbintra.net)
      uri = `${window.location.protocol}//${host}${uri}`
    }
  }

  return uri
}

const reportTiming = (then, now, { url }) => {
  dataLayer({
    event: 'timing',
    category: 'api request',
    label: url,
    value: now - then,
  })
}

const reportTimeout = (exception) => {
  dataLayer({
    event: 'exception',
    exception,
  })
}

export async function apiRequest(...args) {
  if (!process.env.ENABLE_REPORT_REQUEST_TIMING) {
    return makeApiRequest(...args)
  }

  const then = new Date()
  const response = await makeApiRequest(...args)
  const now = new Date()
  const { url } = response
  reportTiming(then, now, { url })
  return response
}
